// Extend HTMLVideoElement interface for Picture-in-Picture API
declare global {
  interface HTMLVideoElement {
    requestPictureInPicture(): Promise<PictureInPictureWindow>;
    disablePictureInPicture: boolean;
  }

  interface Document {
    pictureInPictureElement: Element | null;
    exitPictureInPicture(): Promise<void>;
  }

  interface PictureInPictureWindow extends EventTarget {
    readonly width: number;
    readonly height: number;
  }
}

// The prototype patch every active layer shares; see `patchPiPAPI`.
const sharedPatch: { count: number; original: (() => Promise<PictureInPictureWindow>) | null } = {
  count: 0,
  original: null,
};

export interface AntiPipConfig {
  enabled: boolean;
  pollInterval: number; // milliseconds
  onPipBlocked?: (details: string) => void;
}

export class AntiPipLayer {
  private config: AntiPipConfig;
  private pollInterval: number | null = null;
  private originalRequestPiP: (() => Promise<PictureInPictureWindow>) | null = null;
  private holdsPatch: boolean = false;
  private boundHandlePiPEnter: ((event: Event) => void) | null = null;
  private isActive: boolean = false;
  private protectedElements: Set<HTMLVideoElement> = new Set();
  private mutationObserver: MutationObserver | null = null;

  constructor(config: Partial<AntiPipConfig> = {}) {
    this.config = {
      enabled: true,
      pollInterval: 500,
      ...config,
    };
  }

  /**
   * Check if PiP should be disabled based on URL parameter only
   * (Used as fallback when store value is not available)
   */
  public static isPipDisabledFromUrl(): boolean {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const pipParam = urlParams.get('pip');
      return pipParam === 'false';
    } catch (e) {
      return false; // Default: PiP allowed
    }
  }

  /**
   * Check if PiP should be disabled based on store value or URL parameter
   * @param storeValue - The pipDisabled value from Vuex store (optional)
   */
  public static isPipDisabled(storeValue?: boolean): boolean {
    // Store value takes precedence if explicitly set
    if (typeof storeValue === 'boolean') {
      return storeValue;
    }
    // Fallback to URL check
    return AntiPipLayer.isPipDisabledFromUrl();
  }

  /**
   * Initialize the anti-PiP protection layer
   */
  public initialize(): void {
    if (!this.config.enabled || this.isActive) return;

    this.isActive = true;

    // 0. Exit any existing PiP session immediately
    this.exitPiP();

    // 1. Patch the global PiP API
    this.patchPiPAPI();

    // 2. Listen for PiP events
    this.boundHandlePiPEnter = this.handlePiPEnter.bind(this);
    document.addEventListener('enterpictureinpicture', this.boundHandlePiPEnter);

    // 3. Start periodic polling as fallback
    this.startPolling();

    // 4. Protect all existing video elements on the page
    this.protectAllVideoElements();

    // 5. Watch for new video elements added to the DOM
    this.startMutationObserver();
  }

  /**
   * Find and protect all video elements currently on the page
   */
  private protectAllVideoElements(): void {
    const videos = document.querySelectorAll('video');
    videos.forEach((video) => {
      this.protectVideoElement(video as HTMLVideoElement);
    });
  }

  /**
   * Start watching for new video elements added to the DOM
   */
  private startMutationObserver(): void {
    if (this.mutationObserver) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLVideoElement) {
            this.protectVideoElement(node);
          }
          // Also check children of added nodes
          if (node instanceof HTMLElement) {
            const videos = node.querySelectorAll('video');
            videos.forEach((video) => {
              this.protectVideoElement(video as HTMLVideoElement);
            });
          }
        });
      });
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Stop watching for new video elements
   */
  private stopMutationObserver(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  /**
   * Patch the HTMLVideoElement.prototype.requestPictureInPicture to block PiP.
   * Shared by every active layer: each player (two desktop panes, say) has its
   * own layer, and when each saved and restored "the original" itself, tearing
   * one down unblocked the prototype under the other.
   */
  private patchPiPAPI(): void {
    if (this.holdsPatch) return; // Already patched by this layer

    if (sharedPatch.count === 0) {
      sharedPatch.original = HTMLVideoElement.prototype.requestPictureInPicture;
      HTMLVideoElement.prototype.requestPictureInPicture = function () {
        console.warn('[AntiPiP] Picture-in-Picture is disabled for this session');
        return Promise.reject(new DOMException('Picture-in-Picture is disabled', 'NotAllowedError'));
      };
    }
    sharedPatch.count += 1;
    this.holdsPatch = true;
    this.originalRequestPiP = sharedPatch.original;
  }

  /**
   * Release this layer's hold on the prototype patch; the last one restores it.
   */
  private unpatchPiPAPI(): void {
    if (!this.holdsPatch) return;
    this.holdsPatch = false;
    this.originalRequestPiP = null;

    sharedPatch.count = Math.max(0, sharedPatch.count - 1);
    if (sharedPatch.count === 0) {
      // No original (Firefox has no PiP API): leave the blocking stub in place.
      if (sharedPatch.original) HTMLVideoElement.prototype.requestPictureInPicture = sharedPatch.original;
      sharedPatch.original = null;
    }
  }

  /**
   * Handle PiP enter event - exit immediately
   */
  private handlePiPEnter(event: Event): void {
    this.exitPiP();
    this.config.onPipBlocked?.('PiP enter event intercepted');
  }

  /**
   * Exit PiP mode if active
   */
  public exitPiP(): void {
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch((err) => {
        console.warn('[AntiPiP] Failed to exit PiP:', err);
      });
    }
  }

  /**
   * Start periodic polling to catch any PiP that slipped through
   */
  private startPolling(): void {
    if (this.pollInterval) return;

    this.pollInterval = window.setInterval(() => {
      if (document.pictureInPictureElement) {
        this.exitPiP();
        this.config.onPipBlocked?.('PiP detected via polling');
      }
    }, this.config.pollInterval);
  }

  /**
   * Stop periodic polling
   */
  private stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  /**
   * Apply disablePictureInPicture attribute to a video element
   */
  public protectVideoElement(videoElement: HTMLVideoElement | null): void {
    if (!videoElement || !this.config.enabled) return;

    // Set the native attribute
    videoElement.disablePictureInPicture = true;
    videoElement.setAttribute('disablepictureinpicture', '');

    // Patch the element-specific method
    videoElement.requestPictureInPicture = () => {
      console.warn('[AntiPiP] PiP blocked on protected element');
      return Promise.reject(new DOMException('Picture-in-Picture is disabled', 'NotAllowedError'));
    };

    // Track protected elements
    this.protectedElements.add(videoElement);
  }

  /**
   * Remove protection from a video element
   */
  public unprotectVideoElement(videoElement: HTMLVideoElement | null): void {
    if (!videoElement) return;

    videoElement.disablePictureInPicture = false;
    videoElement.removeAttribute('disablepictureinpicture');

    // Restore original method if we have it
    if (this.originalRequestPiP) {
      videoElement.requestPictureInPicture = this.originalRequestPiP;
    }

    this.protectedElements.delete(videoElement);
  }

  /**
   * Check if the layer is currently active
   */
  public isInitialized(): boolean {
    return this.isActive;
  }

  /**
   * Get the number of protected elements
   */
  public getProtectedCount(): number {
    return this.protectedElements.size;
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<AntiPipConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Destroy the anti-PiP layer and restore original behavior
   */
  public destroy(): void {
    if (!this.isActive) return;

    this.isActive = false;

    // Remove event listener
    if (this.boundHandlePiPEnter) {
      document.removeEventListener('enterpictureinpicture', this.boundHandlePiPEnter);
      this.boundHandlePiPEnter = null;
    }

    // Stop polling
    this.stopPolling();

    // Stop watching for new elements
    this.stopMutationObserver();

    // Restore original API once no other layer still needs it
    this.unpatchPiPAPI();

    // Clear protected elements
    this.protectedElements.clear();
  }
}

// Export a factory function for convenience
export function createAntiPipLayer(
  pipDisabledFromStore?: boolean,
  onPipBlocked?: (details: string) => void
): AntiPipLayer | null {
  // Check if PiP should be disabled (store value takes precedence)
  if (!AntiPipLayer.isPipDisabled(pipDisabledFromStore)) {
    return null;
  }

  const layer = new AntiPipLayer({
    enabled: true,
    pollInterval: 500,
    onPipBlocked,
  });

  layer.initialize();
  return layer;
}

